import React, {useState, useEffect, Fragment} from "react"
import moment from "moment-timezone"
import styled from "styled-components"

// redux
import {useDispatch, useSelector} from "react-redux"
import {bookingDataSelector, clearBookingSlot} from "../../../Bookings/bookingSlice"
import {coachDataSelector} from "../../../Coaches/coachesSlice"

// UI
import {Container, Grid, Row, Col, ContainerFluid} from "@uprise/grid"
import chevronPurple from "@uprise/icons/icons/png/chevron-purple/chevron-purple.png"
import {Card} from "@uprise/card"
import {spacing} from "@uprise/spacing"
import {Button} from "@uprise/button"
import {extended, semantic, primary} from "@uprise/colors"

// components
import DropDown from "components/shared/DropDown"
import Checkbox from "components/shared/Checkbox"
import DatePicker from "components/shared/DatePicker"
import Preloader from "components/shared/Preloader/Preloader"
import {
	ButtonLeft,
	ButtonRight,
	ToggleBtn,
	ToggleBtnRight,
	InlineP
} from "../../../Bookings/CreateBooking/SelectedInfo/index.style"
import SlotsWeekView from "../../../Bookings/CreateBooking/AvailableSlots/SlotsWeekView"

const ButtonContainer = styled.div`
	display: flex;
`

const PrimaryButton = styled(Button)`
	height: 50px;
	outline: none;
`

const MessageStyles = styled.label`
	font-size: 12px;
	color: ${props => {
		if (props.focused) {
			return `${extended.blue.one}`
		} else if (props.validation) {
			return `${semantic.error}`
		} else {
			return `${extended.purple.five}`
		}
	}};
	display: block;
	margin-top: 8px;
	transition: font-size 0.2s;
	padding-left: 40%;
	text-align: left;
`

const Label = styled.span`
	cursor: pointer;
	margin-left: -15px;
`

const HR = styled.div`
	width: 100%;
	height: 1px;
	background: ${primary.purple};
	margin: 10px 0;
`

const Calendar = ({
	coachAvailability,
	selectedCoach,
	timezone,
	selectedDate,
	setSelectedDate,
	coach,
	setTime,
	setTimezone,
	setDuration,
	setCalendarModal
}) => {
	const dispatch = useDispatch()
	const [timezones, setTimezones] = useState([])
	const [active, setActive] = useState(true) //true: 30, false: 60
	const [error, setError] = useState({isError: false, message: "No slot selected!"})
	const [checked, setChecked] = useState(false)
	const [disabled, setDisabled] = useState(true)
	const [showDialog, setShowDialog] = useState(false)

	const {bookingSlot, fetchCoachAvailabilityApiState} = useSelector(bookingDataSelector)

	useEffect(() => {
		setTimezones(moment.tz.names().map(item => ({label: item, value: item})))
	}, [])

	useEffect(() => {
		if (bookingSlot && moment(bookingSlot?.start).tz(timezone).diff(moment().tz(timezone), "minutes") <= 1440) {
			setShowDialog(true)
		} else setShowDialog(false)
	}, [bookingSlot, timezone])

	useEffect(() => {
		if (showDialog && checked) setDisabled(false)
		else if ((showDialog && !checked) || !bookingSlot) setDisabled(true)
		else if (!showDialog) setDisabled(false)
	}, [showDialog, checked, bookingSlot])

	const updateCal = forward => {
		const date = moment(selectedDate)
		if (forward) {
			setSelectedDate(date.endOf("isoWeek").add(7, "days"))
		} else {
			setSelectedDate(date.endOf("isoWeek").subtract(7, "days"))
		}
	}

	const onCancel = () => {
		setError({isError: false, message: "No slot selected!"})
		dispatch(clearBookingSlot())
		setCalendarModal(false)
	}

	const onSave = () => {
		if (!bookingSlot) {
			setError({...error, isError: true})
		} else {
			setError({isError: false, message: "No slot selected!"})
			setCalendarModal(false)
		}
	}

	const handleCheck = e => {
		e.preventDefault()
		setChecked(!checked)
	}

	return (
		<ContainerFluid>
			<Row className='d-flex align-items-center'>
				<Col>
					<HR />
				</Col>
				<Col className='d-flex align-items-center justify-content-start col-6 col-md-6'>
					<DatePicker
						data-testid='datePicker'
						date={selectedDate}
						isLast={true}
						borderRadius={4}
						altIcon={true}
						minDate={moment().startOf("day")}
						onChange={e => setSelectedDate(e.target.value)}
						readOnly={true}
					/>
					<DropDown
						items={timezones}
						label='Select Timezone'
						initialSelectedItem={{label: timezone, value: timezone}}
						onChange={e => setTimezone(e.value)}
						menuStyle={{position: "absolute", zIndex: 2}}
						variant={"white"}
						isLast={true}
						borderRadius={"4px"}
						containerStyle={{width: "240px", marginLeft: 12}}
					/>
				</Col>
				<Col className='d-flex align-items-center justify-content-end col-6 col-md-6'>
					<InlineP>
						{`${moment(selectedDate).startOf("isoWeek").format("D MMMM YYYY")} -
						${moment(selectedDate).endOf("isoWeek").format("D MMMM YYYY")}`}
					</InlineP>
					<ButtonLeft src={chevronPurple} onClick={() => updateCal(false)} />
					<ButtonRight src={chevronPurple} onClick={() => updateCal(true)} />
					<ToggleBtn
						active={active}
						onClick={() => {
							dispatch(clearBookingSlot())
							setDuration(30)
							setActive(true)
						}}>
						30 min
					</ToggleBtn>
					<ToggleBtnRight
						active={!active}
						onClick={() => {
							dispatch(clearBookingSlot())
							setDuration(60)
							setActive(false)
						}}>
						60 min
					</ToggleBtnRight>
				</Col>
				<Col>
					<HR />
				</Col>
			</Row>
			{error.isError && (
				<MessageStyles htmlFor={1} focused={false} validation={error.isError} style={{paddingLeft: 0}}>
					{error.message}
				</MessageStyles>
			)}
			{fetchCoachAvailabilityApiState.isFetching ? (
				<Row className='text-center' style={{minHeight: "300px"}}>
					<Col>
						<Preloader />
					</Col>
				</Row>
			) : (
				<Row className='text-left' style={{minHeight: "300px"}}>
					<SlotsWeekView
						coachAvailability={coachAvailability}
						coach={coach}
						coachSelected={selectedCoach}
						timeZone={timezone}
						week={selectedDate.isoWeek()}
						setTime={() => {}}
						setSelectedCoach={() => {}}
						calView={true}
						weekView={true}
					/>
				</Row>
			)}
			<Row>
				<Col>
					<HR />
				</Col>
			</Row>
			<Row className='align-items-center'>
				<Col className='col-6 col-md-6 col-lg-6'>
					{showDialog && (
						<Row className='align-items-center'>
							<Col className='col-1 col-md-1 col-lg-1'>
								<Checkbox checked={checked} iconYPos={"-25"} onChange={handleCheck} />
							</Col>
							<Col className='col-11 col-md-11 col-lg-11 text-left'>
								<Label onClick={handleCheck}>I have confirmed availability with the clinician</Label>
							</Col>
						</Row>
					)}
				</Col>
				<Col className='col-6 col-md-6'>
					<Row>
						<Col className='col-6 col-md-6'>
							<PrimaryButton variant={"tertiary"} title={"Cancel"} onClick={onCancel} />
						</Col>
						<Col className='col-6 col-md-6'>
							<PrimaryButton variant={"primary"} title={"Save"} onClick={onSave} disabled={disabled} />
						</Col>
					</Row>
				</Col>
			</Row>
		</ContainerFluid>
	)
}

export default Calendar
